/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { FilterState } from './types';
import { SelectionModal, SelectionItem } from '../SelectionModal';
import { formatCpfCnpj } from '../../../constantes';
import {
  FaPlay,
  FaCalendarAlt,
  FaClock,
  FaDatabase,
  FaBuilding,
  FaInfoCircle,
} from 'react-icons/fa';
import { ThreeDot } from 'react-loading-indicators';

interface FilterBarSemanalTempProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  availableYears: number[];
  distribuidores: any[];
  onApply: () => void;
  isLoading: boolean;
}

const MONTHS = [
  { value: 1, label: '01 - Janeiro' },
  { value: 2, label: '02 - Fevereiro' },
  { value: 3, label: '03 - Março' },
  { value: 4, label: '04 - Abril' },
  { value: 5, label: '05 - Maio' },
  { value: 6, label: '06 - Junho' },
  { value: 7, label: '07 - Julho' },
  { value: 8, label: '08 - Agosto' },
  { value: 9, label: '09 - Setembro' },
  { value: 10, label: '10 - Outubro' },
  { value: 11, label: '11 - Novembro' },
  { value: 12, label: '12 - Dezembro' },
];

const WEEKS = [
  { value: 1, label: 'Semana 1 (S1 Acumulado)' },
  { value: 2, label: 'Semana 2 (S1 a S2 Acumulado)' },
  { value: 3, label: 'Semana 3 (S1 a S3 Acumulado)' },
  { value: 4, label: 'Semana 4 (Mês Completo Fechado)' },
];

export const FilterBarSemanalTemp: React.FC<FilterBarSemanalTempProps> = ({
  filters,
  onFilterChange,
  availableYears,
  distribuidores,
  onApply,
  isLoading,
}) => {
  const theme = useTheme() as any;
  const isLight = theme?.name === 'light';
  const [showDistModal, setShowDistModal] = useState(false);

  // Mapeia distribuidores para o SelectionModal
  const distItems: SelectionItem[] = useMemo(() => {
    return (distribuidores || []).map((d: any) => {
      const id = d.id ?? d.value;
      const label = d.fantasy_name || d.nome || d.name || d.label || `Distribuidor ${id}`;
      const subtitle = d.cod_dist
        ? `Código: ${d.cod_dist}`
        : (d.cnpj ? formatCpfCnpj(d.cnpj) : undefined);
      const extra = d.regional || d.uf || undefined;
      return {
        id,
        label,
        subtitle,
        extra,
      };
    });
  }, [distribuidores]);

  const selectedNames = useMemo(() => {
    if (filters.gidDistributorIds.length === 0) {
      return 'Todos os Distribuidores do Canal Indireto (Base GID MSSQL Oficial)';
    }
    const names = (distribuidores || [])
      .filter((d: any) => {
        const id = d.id ?? d.value;
        return filters.gidDistributorIds.some((selectedId) => String(selectedId) === String(id));
      })
      .map((d: any) => d.fantasy_name || d.nome || d.name || d.label);

    if (names.length === 0) {
      return `${filters.gidDistributorIds.length} distribuidor(es) selecionado(s)`;
    }
    if (names.length <= 3) return names.join(', ');
    return `${names.slice(0, 3).join(', ')} e mais ${names.length - 3}...`;
  }, [distribuidores, filters.gidDistributorIds]);

  const handleConfirmDistribuidores = (selectedIds: (string | number)[]) => {
    if (selectedIds.length === 0) {
      onFilterChange({
        ...filters,
        gidDistributorIds: distItems.map((item) => Number(item.id)).filter((n) => !isNaN(n)),
      });
    } else if (selectedIds.includes('__NONE__')) {
      onFilterChange({
        ...filters,
        gidDistributorIds: [],
      });
    } else {
      onFilterChange({
        ...filters,
        gidDistributorIds: selectedIds.map(Number).filter((n) => !isNaN(n)),
      });
    }
    setShowDistModal(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onApply();
  };

  return (
    <div
      style={{
        background: isLight
          ? '#ffffff'
          : 'linear-gradient(135deg, #00205b 0%, #002b80 60%, #001842 100%)',
        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)'}`,
        borderRadius: '18px',
        padding: '20px 24px',
        boxShadow: isLight
          ? '0 4px 16px rgba(0, 0, 0, 0.05)'
          : '0 6px 24px rgba(0, 0, 0, 0.35)',
        fontFamily: 'Montserrat, sans-serif',
      }}
    >
      {/* 1. Header do Card de Filtros */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          paddingBottom: '16px',
          marginBottom: '16px',
          borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h2
            style={{
              fontSize: '0.95rem',
              fontWeight: 800,
              color: isLight ? '#001f5c' : '#ffffff',
              margin: 0,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
            }}
          >
            Filtros do Fechamento Executivo
          </h2>
          <span
            style={{
              fontSize: '0.68rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '9999px',
              background: isLight ? 'rgba(16, 185, 129, 0.12)' : 'rgba(166, 193, 11, 0.18)',
              color: isLight ? '#059669' : '#a6c10b',
              border: `1px solid ${isLight ? 'rgba(16, 185, 129, 0.3)' : 'rgba(166, 193, 11, 0.35)'}`,
            }}
          >
            Base Híbrida (PG + GID)
          </span>
        </div>

        {/* Toggle Semanas Fechadas vs Dias Específicos */}
        <div
          style={{
            display: 'inline-flex',
            background: isLight ? '#f1f5f9' : 'rgba(0, 0, 0, 0.3)',
            padding: '3px',
            borderRadius: '10px',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.1)'}`,
            gap: '4px',
          }}
        >
          <button
            type="button"
            onClick={() => onFilterChange({ ...filters, isCustomPeriod: false })}
            style={{
              padding: '7px 14px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '0.78rem',
              fontWeight: !filters.isCustomPeriod ? 700 : 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              background: !filters.isCustomPeriod
                ? isLight
                  ? '#0284c7'
                  : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)'
                : 'transparent',
              color: !filters.isCustomPeriod
                ? isLight
                  ? '#ffffff'
                  : '#001842'
                : isLight
                ? '#64748b'
                : 'rgba(255, 255, 255, 0.65)',
            }}
          >
            <FaClock size={12} />
            Semanas Fechadas (S1..S4)
          </button>

          <button
            type="button"
            onClick={() => onFilterChange({ ...filters, isCustomPeriod: true })}
            style={{
              padding: '7px 14px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '0.78rem',
              fontWeight: filters.isCustomPeriod ? 700 : 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              background: filters.isCustomPeriod
                ? isLight
                  ? '#0284c7'
                  : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)'
                : 'transparent',
              color: filters.isCustomPeriod
                ? isLight
                  ? '#ffffff'
                  : '#001842'
                : isLight
                ? '#64748b'
                : 'rgba(255, 255, 255, 0.65)',
            }}
          >
            <FaCalendarAlt size={12} />
            Período Customizado (Datas)
          </button>
        </div>
      </div>

      {/* 2. Formulário dos Filtros */}
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px',
            alignItems: 'flex-end',
            marginBottom: '16px',
          }}
        >
          {/* Ano */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: isLight ? '#001f5c' : '#a6c10b',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                marginBottom: '6px',
              }}
            >
              Ano de Referência
            </label>
            <select
              value={filters.year}
              onChange={(e) => onFilterChange({ ...filters, year: Number(e.target.value) })}
              style={{
                width: '100%',
                background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                borderRadius: '10px',
                padding: '9px 12px',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: isLight ? '#0f172a' : '#ffffff',
                fontFamily: 'Montserrat, sans-serif',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {availableYears.map((y) => (
                <option key={y} value={y} style={{ background: isLight ? '#ffffff' : '#002b80' }}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Mês */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: isLight ? '#001f5c' : '#a6c10b',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                marginBottom: '6px',
              }}
            >
              Mês Analisado
            </label>
            <select
              value={filters.month}
              onChange={(e) => onFilterChange({ ...filters, month: Number(e.target.value) })}
              style={{
                width: '100%',
                background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                borderRadius: '10px',
                padding: '9px 12px',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: isLight ? '#0f172a' : '#ffffff',
                fontFamily: 'Montserrat, sans-serif',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value} style={{ background: isLight ? '#ffffff' : '#002b80' }}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Semana ou Datas */}
          {!filters.isCustomPeriod ? (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: isLight ? '#001f5c' : '#a6c10b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  marginBottom: '6px',
                }}
              >
                Semana do Fechamento
              </label>
              <select
                value={filters.week}
                onChange={(e) => onFilterChange({ ...filters, week: Number(e.target.value) })}
                style={{
                  width: '100%',
                  background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                  borderRadius: '10px',
                  padding: '9px 12px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: isLight ? '#0f172a' : '#ffffff',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {WEEKS.map((w) => (
                  <option key={w.value} value={w.value} style={{ background: isLight ? '#ffffff' : '#002b80' }}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: isLight ? '#001f5c' : '#a6c10b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    marginBottom: '6px',
                  }}
                >
                  Data Início
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => onFilterChange({ ...filters, startDate: e.target.value })}
                  style={{
                    width: '100%',
                    background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '8px 12px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: isLight ? '#0f172a' : '#ffffff',
                    fontFamily: 'Montserrat, sans-serif',
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: isLight ? '#001f5c' : '#a6c10b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    marginBottom: '6px',
                  }}
                >
                  Data Fim
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => onFilterChange({ ...filters, endDate: e.target.value })}
                  style={{
                    width: '100%',
                    background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '8px 12px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: isLight ? '#0f172a' : '#ffffff',
                    fontFamily: 'Montserrat, sans-serif',
                    outline: 'none',
                  }}
                />
              </div>
            </>
          )}

          {/* Botão de Atualizar */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                height: '38px',
                borderRadius: '10px',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.82rem',
                fontWeight: 800,
                letterSpacing: '0.4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: isLight
                  ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
                  : 'linear-gradient(135deg, #a6c10b 0%, #8ea709 100%)',
                color: isLight ? '#ffffff' : '#001842',
                boxShadow: isLight
                  ? '0 3px 12px rgba(2, 132, 199, 0.25)'
                  : '0 3px 14px rgba(166, 193, 11, 0.35)',
                transition: 'all 0.2s',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? (
                <ThreeDot color={isLight ? '#ffffff' : '#001842'} size="small" />
              ) : (
                <>
                  <FaPlay size={11} />
                  <span>Atualizar Dados</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Informação sobre Pro-rata */}
        {filters.isCustomPeriod && (
          <div
            style={{
              background: isLight ? '#fffbeb' : 'rgba(245, 158, 11, 0.12)',
              border: `1px solid ${isLight ? '#fde68a' : 'rgba(245, 158, 11, 0.3)'}`,
              borderRadius: '10px',
              padding: '8px 12px',
              marginBottom: '14px',
              fontSize: '0.72rem',
              color: isLight ? '#92400e' : '#fbbf24',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FaInfoCircle size={13} style={{ flexShrink: 0 }} />
            <span>
              <strong>Período Estrito & Pro-rata ativo:</strong> O sellout é apurado estritamente entre <strong>{filters.startDate}</strong> e <strong>{filters.endDate}</strong>, com metas proporcionais homologadas contra o mesmo período do Mês Anterior (MxM).
            </span>
          </div>
        )}

        {/* 3. Seleção de Distribuidores GID */}
        <div
          style={{
            background: isLight ? '#f8fafc' : 'rgba(0, 0, 0, 0.2)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'}`,
            borderRadius: '12px',
            padding: '12px 14px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '6px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaDatabase size={13} color={isLight ? '#0284c7' : '#a6c10b'} />
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: isLight ? '#001f5c' : '#ffffff',
                }}
              >
                Parceiros do Canal Indireto (Base GID MSSQL)
              </span>
            </div>
            <span style={{ fontSize: '0.68rem', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
              (Indireto via GID MSSQL; Direto e Key Account via PostgreSQL)
            </span>
          </div>

          <button
            type="button"
            onClick={() => setShowDistModal(true)}
            style={{
              width: '100%',
              padding: '9px 12px',
              borderRadius: '10px',
              background: isLight ? '#ffffff' : 'rgba(0, 0, 0, 0.25)',
              border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.15)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'border-color 0.2s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              <FaBuilding size={14} color={isLight ? '#0284c7' : '#a6c10b'} />
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <span
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: isLight ? '#0f172a' : '#ffffff',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {filters.gidDistributorIds.length === 0
                    ? 'Todos os Distribuidores do Indireto (Base GID MSSQL Oficial)'
                    : `${filters.gidDistributorIds.length} Distribuidor(es) Selecionado(s) no GID`}
                </span>
                <span
                  style={{
                    fontSize: '0.68rem',
                    color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.55)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedNames}
                </span>
              </div>
            </div>

            <span
              style={{
                fontSize: '0.68rem',
                fontWeight: 800,
                padding: '3px 8px',
                borderRadius: '6px',
                background: isLight ? 'rgba(2, 132, 199, 0.12)' : 'rgba(166, 193, 11, 0.18)',
                color: isLight ? '#0369a1' : '#a6c10b',
                border: `1px solid ${isLight ? 'rgba(2, 132, 199, 0.3)' : 'rgba(166, 193, 11, 0.35)'}`,
                flexShrink: 0,
              }}
            >
              {filters.gidDistributorIds.length === 0 ? 'TODOS GID' : `${filters.gidDistributorIds.length} NO GID`}
            </span>
          </button>
        </div>
      </form>

      {/* Modal de Seleção */}
      <SelectionModal
        isOpen={showDistModal}
        title="Selecionar Distribuidores para puxar do GID MSSQL"
        icon={<FaBuilding size={18} color={isLight ? '#0284c7' : '#a6c10b'} />}
        items={distItems}
        selectedIds={filters.gidDistributorIds.length === 0 ? ['__NONE__'] : filters.gidDistributorIds}
        onConfirm={handleConfirmDistribuidores}
        onClose={() => setShowDistModal(false)}
        allLabel="Todos os Distribuidores do GID"
      />
    </div>
  );
};
